import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { formsApi } from '@/api/formsApi'
import { lisApi, type ApplyTestResultsResult } from '@/api/lisApi'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  initialReceivingProduct,
  labTargets,
  missingLabValues,
  receivingProducts,
  stagesOnForm,
} from '@/lib/lis/applyTestResults'
import { cn } from '@/lib/utils'
import type { FormDefinition, FormSubmission } from '@/types/cgt'
import type { CreatedProduct } from '@/types/products'

/**
 * Apply Test Results (z8nrz7g2wm): laboratory values typed on this form by
 * hand — no LIS automation, or the connection is down — given to one product.
 *
 * The values are entered on the form first, the ordinary way. Here the person
 * says which stage they are for and which product receives them. A value of
 * that stage still empty does not stop them: the dialog asks for it right
 * there, and it is saved to the form (audited like any edit) before the results
 * are applied. The confirmation names the product before the e-signature.
 * Separate from Order Test Results, which asks the LIS.
 */
export function ApplyTestResultsDialog({
  open,
  onOpenChange,
  definition,
  submission,
  products,
  signerName,
  onValuesSaved,
  onApplied,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  definition: FormDefinition
  submission: FormSubmission
  /** All of the procedure's products; the ones that cannot receive results are left out here. */
  products: readonly CreatedProduct[]
  signerName?: string
  /** Values entered in the dialog were saved to the form; this is the form now. */
  onValuesSaved: (submission: FormSubmission) => void
  onApplied: (result: ApplyTestResultsResult) => void
}) {
  const stages = useMemo(() => stagesOnForm(definition), [definition])
  const receivers = useMemo(() => receivingProducts(products), [products])

  const [stage, setStage] = useState<string | null>(null)
  const [productId, setProductId] = useState<string | null>(null)
  const [entered, setEntered] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fresh each time it opens: which product and stage is a decision per apply.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setStage(stages.length === 1 ? stages[0] : null)
      setProductId(initialReceivingProduct(receivers, submission.workingProductIds ?? [])?.id ?? null)
      setEntered({})
      setError(null)
    }
    wasOpen.current = open
  }, [open, stages, receivers, submission.workingProductIds])

  const needsStage = stages.length > 0
  const stageChosen = !needsStage || stage !== null
  const scope = needsStage ? stage : null
  const targets = useMemo(
    () => (stageChosen ? labTargets(definition, scope) : []),
    [definition, scope, stageChosen],
  )
  const missing = useMemo(
    () => (stageChosen ? missingLabValues(definition, scope, submission.values ?? {}) : []),
    [definition, scope, stageChosen, submission.values],
  )
  const product = receivers.find(p => p.id === productId) ?? null

  // Missing values the dialog cannot take (a dropdown, a checkbox) go on the form.
  const formOnly = missing.filter(m => m.inputType === null)
  const stillEmpty = missing.filter(m => m.inputType !== null && (entered[m.id] ?? '').trim() === '')

  const filled = targets.filter(t => !missing.includes(t) && submission.values?.[t.id] != null && submission.values[t.id] !== '')
  const valueCount = filled.length + missing.length - stillEmpty.length - formOnly.length

  const apply = async (signingToken: string) => {
    if (!product) return
    setBusy(true)
    setError(null)
    try {
      // What was typed here goes on the form first, as an ordinary audited
      // edit, so the server finds every value of the stage filled.
      for (const m of missing) {
        const value = (entered[m.id] ?? '').trim()
        if (!value) continue
        const saved = await formsApi.saveFieldValue(submission.id, m.id, value, 'Entered in Apply Test Results')
        onValuesSaved(saved.submission)
      }
      const result = await lisApi.applyTestResults({
        submissionId: submission.id,
        productId: product.id,
        ...(scope ? { stage: scope } : {}),
        signingToken,
      })
      onApplied(result)
      onOpenChange(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'The test results could not be applied.'))
    } finally {
      setBusy(false)
    }
  }

  const blocked =
    receivers.length === 0
      ? 'This procedure has no product that can receive results. Create the product first.'
      : stageChosen && targets.length === 0
        ? needsStage
          ? `Nothing on this form is a laboratory value at stage ${scope}.`
          : 'Nothing on this form is connected to a test code. Set a lab stage or a test code on its fields in the form builder.'
        : null

  return (
    <SignatureDialog
      open={open}
      onOpenChange={onOpenChange}
      title='Apply Test Results'
      description='Give the results entered on this form to one product, as if they had come from the LIS.'
      confirmLabel='Sign & Apply'
      signerName={signerName}
      confirmDisabled={
        !stageChosen || !product || stillEmpty.length > 0 || formOnly.length > 0 || !!blocked
      }
      busy={busy}
      error={error}
      onConfirm={token => void apply(token)}
    >
      <div className='space-y-4'>
        {needsStage && (
          <div className='space-y-1.5'>
            <Label htmlFor='apply-results-stage'>Lab stage</Label>
            <Select value={stage ?? undefined} onValueChange={setStage}>
              <SelectTrigger id='apply-results-stage' className='h-9'>
                <SelectValue placeholder='Choose the stage these results are for' />
              </SelectTrigger>
              <SelectContent>
                {stages.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {receivers.length > 1 && (
          <fieldset className='space-y-1.5'>
            <legend className='text-sm font-medium'>Product receiving the results</legend>
            <div className='max-h-48 space-y-1 overflow-y-auto rounded-md border p-1'>
              {receivers.map(p => (
                <label
                  key={p.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted',
                    p.id === productId && 'bg-muted',
                  )}
                >
                  <input
                    type='radio'
                    name='apply-results-product'
                    checked={p.id === productId}
                    onChange={() => setProductId(p.id)}
                  />
                  <span className='font-mono'>
                    {p.din} / {p.productDescriptionCode}
                  </span>
                  {p.volumeMl != null ? <span className='text-muted-foreground'>{p.volumeMl} mL</span> : null}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {blocked ? (
          <p className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            {blocked}
          </p>
        ) : missing.length > 0 && (stillEmpty.length > 0 || formOnly.length > 0 || !product) ? (
          <div className='space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950'>
            <p className='font-medium text-amber-900 dark:text-amber-200'>
              {missing.length === 1 ? 'This result is' : 'These results are'} not on the form yet. Enter{' '}
              {missing.length === 1 ? 'it' : 'them'} here:
            </p>
            {missing.map(m => (
              <div key={m.id} className='space-y-1'>
                <Label htmlFor={`apply-results-${m.id}`} className='text-xs'>
                  {m.label}
                  {m.testCode ? <span className='ml-1 font-mono text-muted-foreground'>({m.testCode})</span> : null}
                </Label>
                {m.inputType ? (
                  <Input
                    id={`apply-results-${m.id}`}
                    type={m.inputType}
                    className='h-8 bg-background'
                    value={entered[m.id] ?? ''}
                    onChange={e => setEntered(prev => ({ ...prev, [m.id]: e.target.value }))}
                  />
                ) : (
                  <p className='text-xs text-amber-900 dark:text-amber-200'>Enter this one on the form.</p>
                )}
              </div>
            ))}
          </div>
        ) : product && stageChosen ? (
          <p className='flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm'>
            <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' />
            <span>
              <span className='font-mono font-medium'>
                {product.din} / {product.productDescriptionCode}
              </span>{' '}
              will receive these test results
              {scope ? ` for stage ${scope}` : ''}: {valueCount} value
              {valueCount === 1 ? '' : 's'}.
            </span>
          </p>
        ) : null}
      </div>
    </SignatureDialog>
  )
}
